import { z } from 'zod'
import { PersistedDateRangeSchema } from '../persistedDateRange'
import { ElasticFilterSchema } from '../filter'
import { AreaColumnIdSchema } from '../area/columns'
import { AreaListFiltersSchema, AreaListSortBySchema, AreaListSortOrderSchema } from '../area/api'
import { AccountTableColumnIdSchema, AccountTableSortColumnIdSchema } from '../account/tableColumns'
import { AccountSortOrderSchema, AccountStatusSearchInputSchema } from '../account/model'
import { AccountGroupByOptionSchema } from '../account/groupBy'

export const ViewSurfaceSchema = z.enum(['areaManagement', 'accountList'])
export type ViewSurface = z.infer<typeof ViewSurfaceSchema>

// Saved filters hold date rules instead of resolved timestamps, and never the map bounding box
export const AreaManagementFilterValuesSchema = z.object({
  ...AreaListFiltersSchema.pick({
    userId: true,
    teamIds: true,
    downlineTeamIds: true,
    query: true,
    areaStates: true,
  }).shape,
  showAllAreas: z.boolean(),
  createdDate: PersistedDateRangeSchema,
  lastAssignmentDate: PersistedDateRangeSchema,
  expirationDate: PersistedDateRangeSchema,
})
export type AreaManagementFilterValues = z.infer<typeof AreaManagementFilterValuesSchema>

export const AreaManagementAccountFilterValuesSchema = z.object({
  ...ElasticFilterSchema.pick({
    workflowId: true,
    workflowStageIds: true,
    statusIds: true,
    cities: true,
    states: true,
    zipCodes: true,
    customFields: true,
    advancedCustomFields: true,
  }).shape,
  lastActivityDate: PersistedDateRangeSchema.optional(),
})
export type AreaManagementAccountFilterValues = z.infer<typeof AreaManagementAccountFilterValuesSchema>

export const AccountListFilterValuesSchema = z.object({
  ...AccountStatusSearchInputSchema.pick({
    workflowId: true,
    stageIds: true,
    statusIds: true,
    teamId: true,
    teamIds: true,
    userId: true,
    searchByCurrentStage: true,
    query: true,
  }).shape,
  dateRange: PersistedDateRangeSchema,
})
export type AccountListFilterValues = z.infer<typeof AccountListFilterValuesSchema>

export const AreaViewSortColumnIdSchema = AreaListSortBySchema.exclude(['order'])
export type AreaViewSortColumnId = z.infer<typeof AreaViewSortColumnIdSchema>

export const AreaViewSortSchema = z.object({ columnId: AreaViewSortColumnIdSchema, direction: AreaListSortOrderSchema })
export type AreaViewSort = z.infer<typeof AreaViewSortSchema>

export const AreaManagementViewPayloadSchema = z.object({
  surface: z.literal('areaManagement'),
  filters: AreaManagementFilterValuesSchema,
  accountFilters: AreaManagementAccountFilterValuesSchema.optional(),
  sort: AreaViewSortSchema.optional(),
  columns: AreaColumnIdSchema.array(),
})
export type AreaManagementViewPayload = z.infer<typeof AreaManagementViewPayloadSchema>

export const AccountListViewSortSchema = z.object({
  columnId: AccountTableSortColumnIdSchema,
  direction: AccountSortOrderSchema,
})
export type AccountListViewSort = z.infer<typeof AccountListViewSortSchema>

export const AccountListViewPayloadSchema = z.object({
  surface: z.literal('accountList'),
  filters: AccountListFilterValuesSchema,
  groupBy: AccountGroupByOptionSchema.optional(),
  sort: AccountListViewSortSchema.optional(),
  columns: AccountTableColumnIdSchema.array(),
})
export type AccountListViewPayload = z.infer<typeof AccountListViewPayloadSchema>

export const ViewPayloadSchema = z.discriminatedUnion('surface', [
  AreaManagementViewPayloadSchema,
  AccountListViewPayloadSchema,
])
export type ViewPayload = z.infer<typeof ViewPayloadSchema>
